/**
 * The phone editor's bottom dock and drawing bar — §6.2, board 9.
 *
 * **The dock is the rail, lying down.** Same eight tools, same names, same
 * keys in `TOOL_KEYS` — a phone just has no room for a column, so they go in
 * a pill along the bottom where a thumb reaches. Equal-width cells of at
 * least 44px and 54px tall; below eight of those the pill scrolls sideways
 * rather than shrinking the cells, because a 32px tool is a tool you miss.
 *
 * **While something is being drawn the dock is replaced, not covered.** The
 * drawing bar is a different set of questions — how many points, add another,
 * finish, take one back, give up — and leaving the tools underneath would
 * mean a tap meant for ✓ landing on Circle. Replacing it is also what makes
 * room for the live readout, which is the one number somebody is watching.
 */
import type { ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { Switch } from '../../components/primitives.js'
import { MAP_TOOL_ICONS } from '../../frame/icons.js'
import type { ToolName } from '../draw/index.js'

interface DockTool { name: ToolName; label: string }

const ALL: DockTool[] = [
  { name: 'select', label: 'Select' },
  { name: 'area', label: 'Area' },
  { name: 'rect', label: 'Rect' },
  { name: 'circle', label: 'Circle' },
  { name: 'line', label: 'Line' },
  { name: 'marker', label: 'Marker' },
  { name: 'label', label: 'Label' },
  { name: 'measure', label: 'Measure' },
]

/** §6.2: the measuring set is Select and the four outline tools. */
const MEASURING: ToolName[] = ['select', 'area', 'rect', 'circle', 'line']

export function ToolDock({ tool, canDraw, offerMeasure, measuring, onPick }: {
  tool: ToolName
  canDraw: boolean
  offerMeasure: boolean
  measuring?: boolean
  onPick: (tool: ToolName) => void
}) {
  const shown = ALL
    .filter((one) => (measuring ? MEASURING.includes(one.name) : true))
    .filter((one) => one.name !== 'measure' || offerMeasure)
    .filter((one) => canDraw || one.name === 'select')

  return (
    <div className={cn(
      'pointer-events-auto absolute inset-x-3 bottom-[30px] flex overflow-x-auto rounded-[22px]',
      'bg-surface p-1 shadow-popover no-scrollbar',
    )}
    >
      {shown.map((one) => {
        const Icon = MAP_TOOL_ICONS[one.name]
        const on = tool === one.name
        return (
          <button
            key={one.name}
            type="button"
            aria-pressed={on}
            onClick={() => onPick(one.name)}
            className={cn(
              'flex h-[54px] min-w-[44px] flex-1 shrink-0 flex-col items-center justify-center',
              'gap-1 rounded-[18px] text-[9.5px] font-semibold',
              on ? 'bg-accent-soft text-accent-dark' : 'text-text-body',
            )}
          >
            <Icon size={19} />
            {one.label}
          </button>
        )
      })}
    </div>
  )
}

/**
 * The drawing bar — §6.2 step 1.
 *
 * `Add point` is deliberately the biggest thing on the screen: it is pressed
 * once per corner and every other control here is pressed once per shape.
 * ✓ and ↶ sit beside it rather than above, so the thumb that just placed a
 * point can finish without moving across the screen.
 */
export function DrawingBar({
  readout, hint, layerChip, snap, onSnap, crosshair, onCrosshair,
  canFinish, canUndo, onAdd, onFinish, onUndo, onCancel, compact,
}: {
  /** `3,980 sq ft · Area · 4 points`, live. */
  readout: string
  hint: string
  layerChip?: ReactNode
  snap: boolean
  onSnap: (on: boolean) => void
  /** Present only where the tool has a choice — Rect and Circle (§6.2.3). */
  crosshair?: boolean
  onCrosshair?: (on: boolean) => void
  canFinish: boolean
  canUndo: boolean
  onAdd: () => void
  onFinish: () => void
  onUndo: () => void
  onCancel: () => void
  /**
   * The iPad's shape — §7.
   *
   * A phone gives this bar the bottom of the screen because it has nothing
   * else down there: the dock it replaces was in the same place, and the
   * thumb that presses **Add point** is already at the foot. An iPad has a
   * layer chip, a save pill and the Snap and Crosshair switches along the
   * bottom-left, so a full-width bar there both buried them and drew a
   * second Snap and a second Crosshair two inches from the first.
   *
   * Compact is therefore one row, centred at the **top**, in the place the
   * hint chip it replaces already occupied — the controls where the words
   * about them were — and it leaves the switches to the cluster that has
   * them. The same component either way, so the two cannot drift.
   */
  compact?: boolean
}) {
  if (compact) {
    return (
      <div className={cn(
        'pointer-events-auto absolute left-1/2 top-3 flex max-w-[calc(100%-24px)]',
        '-translate-x-1/2 items-center gap-2.5 rounded-[22px] bg-surface py-1.5 pl-2 pr-1.5',
        'shadow-popover',
      )}
      >
        <BarRound label="Cancel this shape" onClick={onCancel}>✕</BarRound>
        <b className="shrink-0 text-cell tabular-nums">{readout}</b>
        <span className="truncate text-sub text-text-muted">{hint}</span>
        <button
          type="button"
          onClick={onAdd}
          className={cn(
            'h-11 shrink-0 rounded-[16px] bg-accent px-4 text-cell font-semibold text-white',
            'active:bg-accent-dark',
          )}
        >
          Add point
        </button>
        <BarRound label="Remove the last point" disabled={!canUndo} onClick={onUndo}>↶</BarRound>
        <BarRound label="Finish this shape" green disabled={!canFinish} onClick={onFinish}>
          ✓
        </BarRound>
      </div>
    )
  }

  return (
    <div className={cn(
      'pointer-events-auto absolute inset-x-3 bottom-[30px] flex flex-col gap-2 rounded-[22px]',
      'bg-surface p-3 shadow-popover',
    )}
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label="Cancel this shape"
          onClick={onCancel}
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-field',
            'text-cell font-semibold text-text-muted',
          )}
        >
          ✕
        </button>
        <b className="truncate text-cell tabular-nums">{readout}</b>
        {layerChip}
      </div>

      <p className="m-0 text-sub text-text-muted">{hint}</p>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onAdd}
          className={cn(
            'h-14 flex-1 rounded-[16px] bg-accent text-[15px] font-semibold text-white',
            'active:bg-accent-dark',
          )}
        >
          Add point
        </button>
        <button
          type="button"
          aria-label="Remove the last point"
          disabled={!canUndo}
          onClick={onUndo}
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-field',
            'text-[17px] text-text-body disabled:opacity-40',
          )}
        >
          ↶
        </button>
        <button
          type="button"
          aria-label="Finish this shape"
          disabled={!canFinish}
          onClick={onFinish}
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent',
            'text-[17px] font-semibold text-white disabled:opacity-40',
          )}
        >
          ✓
        </button>
      </div>

      <div className="flex items-center gap-4">
        <Switch checked={snap} onChange={onSnap} label="Snap" />
        {onCrosshair && (
          <Switch checked={Boolean(crosshair)} onChange={onCrosshair} label="Crosshair" />
        )}
      </div>
    </div>
  )
}

/** The 44px round buttons the compact bar is made of (§7's touch sizes). */
function BarRound({ label, green, disabled, onClick, children }: {
  label: string
  green?: boolean
  disabled?: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[17px]',
        'disabled:opacity-40',
        green ? 'bg-accent font-semibold text-white' : 'bg-field text-text-body',
      )}
    >
      {children}
    </button>
  )
}

/**
 * The crosshair itself: fixed at the centre, never under a finger.
 *
 * `pointer-events-none` throughout — it is a sight, not a control. Every tap
 * that matters happens on **Add point** down in the bar.
 */
export function CrosshairMark() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
    >
      <div className="relative h-11 w-11">
        <span className="absolute inset-0 rounded-full border-2 border-white/90 shadow-popover" />
        <span className="absolute left-1/2 top-0 h-3 w-0.5 -translate-x-1/2 bg-white/90" />
        <span className="absolute bottom-0 left-1/2 h-3 w-0.5 -translate-x-1/2 bg-white/90" />
        <span className="absolute left-0 top-1/2 h-0.5 w-3 -translate-y-1/2 bg-white/90" />
        <span className="absolute right-0 top-1/2 h-0.5 w-3 -translate-y-1/2 bg-white/90" />
        <span className={cn(
          'absolute left-1/2 top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2',
          'rounded-full bg-accent',
        )}
        />
      </div>
    </div>
  )
}
